import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { LayoutPanelLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { Text, Title, UnstyledButton } from "@/shared/ui";
import { cn } from "@/shared/ui/utils/tailwind";
import {
  fetchDashboardPanels,
  type AggregationResult,
  type DashboardSpec,
} from "@services/housien/dashboards";
import { DashboardPanelView } from "../components/DashboardPanelView";
import { dimensionLabel } from "../data/dimension-labels";
import { DEFAULT_DASHBOARDS } from "../data/default-dashboards";

/**
 * The Dashboards LIST page (seq-12): lists the agency's saved dashboards and,
 * on opening one, re-runs the aggregation LIVE and renders the DS panels.
 *
 * Mounted at `/app/agencies/:agency_id/dashboards`. Reachable from the
 * right-menu "Painéis" entry the CHAT worker wires (see the reconciliation
 * note in the feature index). Numbers are ALWAYS freshly recomputed from the
 * backend `aggregate?group_by=...` buckets — never stale cached numbers
 * (AC-DASH-2) — because opening a dashboard calls {@link fetchDashboardPanels}.
 *
 * @returns The Dashboards list / detail page element.
 */
export function DashboardsListPage() {
  const { agency_id: agencyId = "" } = useParams<{ agency_id: string }>();
  const [openId, setOpenId] = useState<string | null>(null);
  const { t } = useTranslation("features/assistant");

  const dashboards = useMemo<DashboardSpec[]>(() => DEFAULT_DASHBOARDS, []);
  const open = dashboards.find((dashboard) => dashboard.id === openId) ?? null;

  if (open) {
    return (
      <DashboardDetail
        agencyId={agencyId}
        dashboard={open}
        onBack={() => setOpenId(null)}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-(--space-5) p-(--space-5)">
      <header className="flex flex-col gap-(--space-1)">
        <Title order={3} style={{ fontSize: "inherit", fontWeight: "var(--font-bold)" }}>
          {t("dashboards.title")}
        </Title>
        <Text variant="meta" tone="secondary">
          {t("dashboards.subtitle")}
        </Text>
      </header>

      <div
        className="flex flex-col gap-(--space-2)"
        data-testid="dashboards-list"
      >
        {dashboards.map((dashboard) => (
          <UnstyledButton
            key={dashboard.id}
            type="button"
            onClick={() => setOpenId(dashboard.id)}
            data-testid="dashboard-list-item"
            className={cn(
              "flex w-full items-center gap-(--space-3) rounded-(--radius-md) border border-(--border-subtle)",
              "bg-(--surface-raised) p-(--space-4) text-left shadow-[var(--shadow-sm)]",
              "cursor-pointer transition-colors hover:bg-(--surface-hover)",
            )}
          >
            <Text
              component="span"
              className="flex size-9 shrink-0 items-center justify-center rounded-(--radius-md) bg-(--selection-selected) text-(--seed-base)"
              aria-hidden
            >
              <LayoutPanelLeft width={18} height={18} />
            </Text>
            <div className="flex min-w-0 flex-1 flex-col">
              <Text variant="bodySm" fw={700} className="truncate">
                {dashboard.title}
              </Text>
              <Text variant="meta" tone="secondary" className="truncate">
                {dashboard.dimensions.map((dim) => dimensionLabel(dim)).join(" · ")}
              </Text>
            </div>
            <ChevronRight width={18} height={18} className="shrink-0 text-(--text-tertiary)" />
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
}

/**
 * The opened-dashboard view: fetches every panel LIVE from the backend
 * aggregation route and stacks the DS panels. Shows a loading and an honest
 * error state so a failed fetch never renders a fabricated success.
 *
 * @param props.agencyId The agency whose portfolio is aggregated.
 * @param props.dashboard The saved dashboard spec to render.
 * @param props.onBack Handler to return to the list.
 * @returns The dashboard detail element.
 */
function DashboardDetail({
  agencyId,
  dashboard,
  onBack,
}: {
  agencyId: string;
  dashboard: DashboardSpec;
  onBack: () => void;
}) {
  const [panels, setPanels] = useState<AggregationResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("features/assistant");

  const load = useCallback(() => {
    setPanels(null);
    setError(null);
    fetchDashboardPanels(agencyId, dashboard.dimensions)
      .then(setPanels)
      .catch(() => setError(t("dashboards.loadError")));
  }, [agencyId, dashboard.dimensions, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex w-full flex-col gap-(--space-5) p-(--space-5)">
      <header className="flex items-center gap-(--space-3)">
        <UnstyledButton
          type="button"
          onClick={onBack}
          aria-label={t("dashboards.backAria")}
          className="inline-flex cursor-pointer items-center justify-center rounded-(--radius-sm) p-(--space-1-5) text-(--text-secondary) transition-colors hover:bg-(--surface-hover)"
        >
          <ArrowLeft width={18} height={18} />
        </UnstyledButton>
        <div className="flex min-w-0 flex-1 flex-col">
          <Title
            order={3}
            className="truncate"
            style={{ fontSize: "inherit", fontWeight: "var(--font-bold)" }}
          >
            {dashboard.title}
          </Title>
          <Text variant="meta" tone="secondary">
            {t("dashboards.detailSubtitle")}
          </Text>
        </div>
      </header>

      {error && (
        <Text variant="meta" tone="secondary" data-testid="dashboard-error">
          {error}
        </Text>
      )}

      {!error && panels === null && (
        <Text variant="meta" tone="secondary" data-testid="dashboard-loading">
          {t("dashboards.loading")}
        </Text>
      )}

      {!error && panels !== null && (
        <div
          className="grid grid-cols-1 gap-(--space-4) lg:grid-cols-2"
          data-testid="dashboard-panels"
        >
          {panels.map((result) => (
            <DashboardPanelView key={result.groupBy} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}

export default DashboardsListPage;
